/**
 * useAudioEffects - Sound effects using Web Audio API
 * 
 * Generates sounds programmatically - no external files needed
 */

import { useCallback, useRef, useEffect } from 'react';

export function useAudioEffects(enabled = true) {
  const audioContextRef = useRef(null);

  // Initialize AudioContext on first user interaction
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Play a tone
  const playTone = useCallback((frequency, duration, type = 'sine', volume = 0.3) => {
    if (!enabled) return;
    
    try {
      const ctx = getAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
      
      // Envelope
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }
  }, [enabled, getAudioContext]);

  // Success sound - cheerful ascending notes
  const playSuccess = useCallback(() => {
    if (!enabled) return;
    playTone(523, 0.15, 'sine', 0.25); // C5
    setTimeout(() => playTone(659, 0.15, 'sine', 0.25), 100); // E5
    setTimeout(() => playTone(784, 0.25, 'sine', 0.3), 200); // G5
  }, [enabled, playTone]);

  // Click sound - soft pop
  const playClick = useCallback(() => {
    if (!enabled) return;
    playTone(800, 0.08, 'sine', 0.15);
  }, [enabled, playTone]);

  // Whoosh sound - sweep
  const playWhoosh = useCallback(() => {
    if (!enabled) return;
    
    try {
      const ctx = getAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(200, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.25);
    } catch (e) {}
  }, [enabled, getAudioContext]);

  // Pop sound
  const playPop = useCallback(() => {
    if (!enabled) return;
    playTone(400, 0.1, 'sine', 0.2);
    setTimeout(() => playTone(300, 0.08, 'sine', 0.15), 50);
  }, [enabled, playTone]);

  // Fanfare - victory sound
  const playFanfare = useCallback(() => {
    if (!enabled) return;
    playTone(523, 0.2, 'sine', 0.25); // C5
    setTimeout(() => playTone(659, 0.2, 'sine', 0.25), 150); // E5
    setTimeout(() => playTone(784, 0.2, 'sine', 0.25), 300); // G5
    setTimeout(() => playTone(1047, 0.4, 'sine', 0.3), 450); // C6
  }, [enabled, playTone]);

  // Munch sound - eating
  const playMunch = useCallback(() => {
    if (!enabled) return;
    playTone(150, 0.1, 'square', 0.15);
    setTimeout(() => playTone(120, 0.1, 'square', 0.12), 150);
    setTimeout(() => playTone(180, 0.1, 'square', 0.15), 300);
  }, [enabled, playTone]);

  // Gulp sound - drinking
  const playGulp = useCallback(() => {
    if (!enabled) return;
    
    try {
      const ctx = getAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(400, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.15);
      
      gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.2);
    } catch (e) {}
  }, [enabled, getAudioContext]);

  // Fridge sound - door open/close
  const playFridge = useCallback(() => {
    if (!enabled) return;
    
    try {
      const ctx = getAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(100, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(60, ctx.currentTime + 0.3);
      
      gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.35);
    } catch (e) {}
  }, [enabled, getAudioContext]);

  return {
    playSuccess,
    playClick,
    playWhoosh,
    playPop,
    playFanfare,
    playMunch,
    playGulp,
    playFridge,
  };
}

export default useAudioEffects;
